import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, ArrowRight, Droplet, Leaf, Sprout, FlaskConical } from "lucide-react";
import { useImg } from "@/lib/useImg";

const SLIDES = [
  {
    imgKey: "homeNewHero1",
    badge: "New Launch",
    lines: ["Natural Support for", "Balanced Blood Sugar"],
    sub: "Wholesome, plant-based superfoods for a healthier you.",
    features: [
      { icon: Droplet, label: "Healthy Blood Sugar" },
      { icon: Leaf, label: "Rich in Nutrients" },
      { icon: Sprout, label: "100% Plant-Based" },
      { icon: FlaskConical, label: "No Preservatives" },
    ],
    cta: { label: "Our Story", to: "/our-story" },
  },
  {
    imgKey: "homeNewHero2",
    badge: "Everyday Wellness",
    lines: ["Nature's Finest", "Green Superfoods"],
    sub: "Pure moringa and greens, thoughtfully crafted to fuel your day.",
    features: [
      { icon: Leaf, label: "Nutrient Dense" },
      { icon: Sprout, label: "Freshly Sourced" },
      { icon: Droplet, label: "Daily Energy" },
    ],
    cta: { label: "Why Nutribest", to: "/experience" },
  },
  {
    imgKey: "homeNewHero3",
    badge: "Snack Smarter",
    lines: ["Seeds, Nuts &", "Berry Goodness"],
    sub: "Crunchy, antioxidant-rich blends for wholesome everyday snacking.",
    features: [
      { icon: Leaf, label: "Antioxidant Rich" },
      { icon: Sprout, label: "Sustainably Sourced" },
      { icon: FlaskConical, label: "No Additives" },
    ],
    cta: { label: "Shop Now", to: "/shop-by-category" },
  },
];

const variants = {
  enter: (d) => ({ x: d >= 0 ? "100%" : "-100%" }),
  center: { x: 0 },
  exit: (d) => ({ x: d >= 0 ? "-100%" : "100%" }),
};

export const HomeNewHero = () => {
  const img = useImg();
  const [i, setI] = useState(0);
  const [dir, setDir] = useState(1);
  const [auto, setAuto] = useState(true);

  const go = (n, d) => {
    setAuto(false);
    setDir(d);
    setI((n + SLIDES.length) % SLIDES.length);
  };

  useEffect(() => {
    if (!auto) return;
    const t = setTimeout(() => {
      setDir(1);
      setI((p) => (p + 1) % SLIDES.length);
    }, 5500);
    return () => clearTimeout(t);
  }, [i, auto]);

  const slide = SLIDES[i];

  return (
    <section data-testid="home-new-hero" className="relative overflow-hidden bg-[#F4EFE2]">
      <div className="relative w-full aspect-[1264/848] sm:aspect-[1264/700] lg:aspect-[1264/560]">
        <AnimatePresence initial={false} custom={dir}>
          <motion.div
            key={i}
            data-testid={`home-new-hero-slide-${i + 1}`}
            className="absolute inset-0"
            custom={dir}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ x: { duration: 0.6, ease: [0.4, 0, 0.2, 1] } }}
          >
            <img src={img(slide.imgKey)} alt="" className="absolute inset-0 w-full h-full object-cover object-right" draggable="false" />
            {/* soft veil — keeps text legible while letting the botanical artwork show through behind it */}
            <div className="absolute inset-0" style={{ background: "linear-gradient(to right, rgba(244,239,226,0.68) 0%, rgba(244,239,226,0.38) 40%, rgba(244,239,226,0) 70%)" }} />

            <div className="relative h-full flex items-center">
              <div className="container-nb">
                <motion.div
                  className="max-w-[60%] sm:max-w-[52%] lg:max-w-[46%]"
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.15 }}
                >
                  <span className="inline-flex items-center gap-2 bg-[#D7E3B8] text-forest text-[9px] sm:text-[11px] font-bold uppercase tracking-[0.18em] px-3 sm:px-4 py-1 sm:py-1.5 rounded-full">
                    <Leaf size={12} /> {slide.badge}
                  </span>
                  <h1 className="font-serif text-forest leading-[1.05] mt-3 sm:mt-4 text-2xl sm:text-4xl lg:text-5xl">
                    {slide.lines[0]}<br />
                    <span className="italic">{slide.lines[1]}</span>
                  </h1>
                  <p className="text-ink/70 mt-2 sm:mt-4 text-xs sm:text-base max-w-sm leading-relaxed hidden sm:block">{slide.sub}</p>

                  <div className="hidden sm:flex flex-wrap gap-x-5 gap-y-2 mt-5">
                    {slide.features.map(({ icon: Icon, label }) => (
                      <span key={label} className="inline-flex items-center gap-2 text-[11px] font-semibold text-forest/80">
                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-forest/25 text-forest"><Icon size={13} /></span>
                        {label}
                      </span>
                    ))}
                  </div>

                  <Link
                    to={slide.cta.to}
                    data-testid="home-new-hero-cta"
                    className="group inline-flex items-center gap-2 bg-forest hover:bg-gold text-cream text-[10px] sm:text-xs font-semibold uppercase tracking-[0.16em] px-5 sm:px-8 py-2.5 sm:py-4 rounded-full mt-4 sm:mt-7 transition-colors"
                  >
                    {slide.cta.label} <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        <button onClick={() => go(i - 1, -1)} data-testid="home-new-hero-prev" aria-label="Previous slide" className="absolute left-3 sm:left-5 top-1/2 -translate-y-1/2 z-30 w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-forest/90 text-cream flex items-center justify-center hover:bg-gold transition-colors shadow-md">
          <ChevronLeft size={20} />
        </button>
        <button onClick={() => go(i + 1, 1)} data-testid="home-new-hero-next" aria-label="Next slide" className="absolute right-3 sm:right-5 top-1/2 -translate-y-1/2 z-30 w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-forest/90 text-cream flex items-center justify-center hover:bg-gold transition-colors shadow-md">
          <ChevronRight size={20} />
        </button>

        <div className="absolute bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2.5">
          {SLIDES.map((_, n) => (
            <button key={n} onClick={() => go(n, n > i ? 1 : -1)} data-testid={`home-new-hero-dot-${n + 1}`} aria-label={`Go to slide ${n + 1}`} className={`w-2.5 h-2.5 rounded-full transition-colors duration-300 ${i === n ? "bg-gold" : "bg-forest/30 hover:bg-forest/60"}`} />
          ))}
        </div>
      </div>
    </section>
  );
};
